import React, { useState } from 'react';
import classnames from 'classnames';
import styled from 'styled-components';
import ReactTooltip from 'react-tooltip';
import MoonLoader from 'react-spinners/MoonLoader';

export type IconTypes = 'save' | 'print' | 'delete' | 'add' | 'update' | 'csv' | 'check' | 'cancel' | 'download' | 'upload' | 'search' | 'refresh' | 'push' | 'image';

interface Props {
    onClick: () => void;
    type: IconTypes,
    className?: string;
    disabled?: boolean;
    baseline?: boolean;
    size?: number;
    disabledDataTip?: string;
    text?: string;
    loading?: boolean;
    simulateLoading?: boolean;
    confirm?: boolean;
    confirmMessage?: string;
    confirmSimple?: boolean;
}

interface MapProps {
    icon: string;
    color: string;
    tip: string;
}

const map: Record<IconTypes, MapProps> = {
    save: {
        icon: 'fa-floppy-disk',
        color: 'success',
        tip: 'Save'
    },
    print: {
        icon: 'fa-print',
        color: 'success',
        tip: 'Print'
    },
    csv: {
        icon: 'fa-file-download',
        color: 'success',
        tip: 'Export to CSV'
    },
    delete: {
        icon: 'fa-trash-can',
        color: 'danger',
        tip: 'Delete'
    },
    add: {
        icon: 'fa-plus-circle',
        color: 'success',
        tip: 'Add'
    },
    update: {
        icon: 'fa-edit',
        color: 'success',
        tip: 'Update'
    },
    check: {
        icon: 'fa-duotone fa-circle-check',
        color: 'success',
        tip: 'Confirm'
    },
    cancel: {
        icon: 'fa-duotone fa-circle-xmark',
        color: 'danger',
        tip: 'Cancel'
    },
    download: {
        icon: 'fa-duotone fa-cloud-arrow-down',
        color: 'success',
        tip: 'Download'
    },
    upload: {
        icon: 'fa-duotone fa-cloud-arrow-up',
        color: 'success',
        tip: 'Upload'
    },
    search: {
        icon: 'fad fa-search',
        color: 'success',
        tip: 'Search'
    },
    refresh: {
        icon: 'fad fa-arrows-rotate',
        color: 'success',
        tip: 'Refresh'
    },
    push: {
        icon: 'fad fa-solid fa-up',
        color: 'success',
        tip: 'Push'
    },
    image: {
        icon: 'fa-duotone fa-image',
        color: 'success',
        tip: 'View Image'
    }
}

const loadingColor: Record<string, string> = {
    success: '#08C408',
    danger: 'red'
}

export default function ActionIcon({
    onClick,
    className,
    type,
    disabled,
    baseline,
    size,
    disabledDataTip,
    text,
    loading,
    simulateLoading,
    confirm,
    confirmMessage,
    confirmSimple
}: Props) {

    const [internalLoading, setInternalLoading] = useState(false);
    const [showingConfirmation, setShowingConfirmation] = useState(false);

    const handleClick = () => {
        if (confirm && !showingConfirmation) {
            setShowingConfirmation(true);
            return;
        }

        onClick();
        if (simulateLoading) {
            setInternalLoading(true);
            setTimeout(() => {
                setInternalLoading(false);
            }, 2000);
        }

        // Reset confirmation state after action
        if (showingConfirmation) {
            setShowingConfirmation(false);
        }
    }

    const handleCancel = () => {
        setShowingConfirmation(false);
    }

    if (loading || internalLoading) {
        return (
            <MoonLoader
                size={28}
                color={loadingColor[map[type].color] || 'black'}
            />
        );
    }

    if (showingConfirmation) {
        if (confirmSimple) {
            return (
                <FlexContainer gap={10}>
                    <Icon
                        className='fa-sharp-duotone fa-solid fa-circle-xmark text-danger'
                        onClick={handleCancel}
                    />
                    <Icon
                        className='fa-duotone fa-solid fa-circle-check'
                        onClick={handleClick}
                    />
                </FlexContainer>
            );
        }

        return (
            <ConfirmationContainer>
                <ConfirmationMessage>{confirmMessage || 'Are you sure?'}</ConfirmationMessage>
                <ButtonContainer>
                    <CancelButton
                        onClick={handleCancel}
                        className="btn btn-sm btn-secondary"
                    >
                        Cancel
                    </CancelButton>
                    <ConfirmButton
                        onClick={handleClick}
                        className="btn btn-sm btn-success me-2"
                    >
                        Confirm
                    </ConfirmButton>
                </ButtonContainer>
            </ConfirmationContainer>
        );
    }

    return (
        <FlexContainer
            onClick={() => !disabled && handleClick()}
            className={classnames(
                { 'custom-disabled': disabled }
            )}
        >
            <Icon
                className={
                    classnames(
                        'fa-duotone',
                        map[type].icon,
                        `text-${map[type].color}`,
                        className
                    )
                }
                data-tip={disabled ? (disabledDataTip || '') : map[type].tip}
                baseline={baseline}
                size={size}
            />
            {
                text && <Text className={'mt-2 ml-1 hover'}>{text}</Text>
            }
            <ReactTooltip />
        </FlexContainer >
    );
}

interface IconProps {
    baseline?: boolean;
    size?: number;
}

const Icon = styled.i<IconProps>`
    font-size: ${p => p.size ? p.size + 'px' : '36px'};
    position: ${p => p.baseline ? 'relative' : null};
    top: ${p => p.baseline ? '10px' : null};

    &:hover {
        cursor: pointer;
    }
`;

const FlexContainer = styled.div<{ gap?: number; }>`
    display: flex;
    align-items: center;
    gap: ${p => p.gap + 'px' || null};
`;

const ConfirmationContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 10px;
    border: 1px solid #ddd;
    border-radius: 4px;
    background-color: #f8f9fa;
    max-width: 300px;
`;

const ConfirmationMessage = styled.div`
    margin-bottom: 10px;
    font-size: 14px;
    text-align: center;
    color: #333;
`;

const ButtonContainer = styled.div`
    display: flex;
    gap: 8px;
`;

const ConfirmButton = styled.button`
    // Bootstrap classes will handle the styling
`;

const CancelButton = styled.button`
    // Bootstrap classes will handle the styling
`;

const Text = styled.h6`
    transition: text-shadow 0.3s ease;

    &:hover {
        /* Adds 1px of "thickness" to the right */
        text-shadow: 0.5px 0 0 currentColor; 
    }
`;